import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { auth } from 'fb/index'

/**
 * Daseruno-api (NestJS REST) 向けの共通 axios クライアント。
 *
 * Phase 0 で雛形のみ用意。実際に slice / Template から呼び出すのは Phase 2
 * (Cloud Functions の REST 化) 以降。
 *
 * 機能:
 *   - Firebase ID Token を Authorization ヘッダーに自動付与
 *   - 401/5xx 等を統一的に AxiosError として throw (raw axios の挙動を維持)
 *   - REACT_APP_API_BASE_URL でエンドポイント切替
 */

const DEFAULT_TIMEOUT_MS = 30_000

/**
 * Base URL の解決優先順位:
 *   1. REACT_APP_API_BASE_URL (本番/staging で .env から注入)
 *   2. http://localhost:3001 (ローカル daseruno-api を起動している前提のフォールバック)
 */
export const apiBaseUrl =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Request interceptor: 認証済みユーザーがいれば Firebase ID Token を付与する。
 *
 * 認証不要エンドポイント (health check 等) を呼ぶ場合でもトークンが付くだけで
 * API 側で害は無いので、エンドポイント別の分岐は入れない。
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const user = auth.currentUser
    if (user) {
      try {
        const idToken = await user.getIdToken()
        config.headers.set('Authorization', `Bearer ${idToken}`)
      } catch (error) {
        // ID トークン取得失敗時はヘッダなしで送る (API 側で 401 になる)
        // eslint-disable-next-line no-console
        console.warn('[apiClient] Failed to get Firebase ID token', error)
      }
    }
    return config
  },
)

/**
 * Response interceptor: 現状は素通し。エラー時の挙動 (401 → ログイン誘導、
 * 5xx → Sentry/Slack 通知) は Phase 2 以降で利用パターンが固まってから追加する。
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => Promise.reject(error),
)
